import { BidGroup } from "../../interfaces/BidTabs";
import { CostModel, CostModelTotalsLite } from "../../interfaces/CostModel";
import { Estimate } from "../../interfaces/Estimate";
import { GCGR, GCGRLineItem, StaffDetail } from "../../interfaces/GCGRs";
import { EstimateData } from "../../interfaces/EstimateData";
import { costModelLabel } from "../../utils/format";
import { excelDateToEpochMs, getContractingReportData, getEstimateReportData } from "../workbook";
import { workbookCache } from "../workbook/cache";
import { DataCacheKey, RetiredCacheKey } from "../workbook/cacheKeys";
import { getLoadedProjectId } from "../workbook/state";

export interface EstimateCardSummary {
  id: string;
  name: string;
  costOfTradesSubtotal: number;
  grossArea: number;
  milestoneName: string;
  lastRefreshedAt: string | undefined;
}

export interface GcgrCardSummary {
  id: string;
  name: string;
  contractValue: number;
  generalRequirements: number;
  generalConditions: number;
  preconStaffing: number;
  constructionStaffing: number;
  lastRefreshedAt: string | undefined;
}

export interface ContractingCardSummary {
  projectedBuy: number;
  budget: number;
  buyValue: number;
  packageCount: number;
  awardedCount: number;
  lastRefreshedAt: string | undefined;
}

export interface CostModelCardSummary {
  id: string;
  label: string;
  grandTotal: number;
  grossArea: number;
  costPerSqft: number;
  lastRefreshedAt: string | undefined;
}

export interface CardSummaries {
  estimate: EstimateCardSummary;
  gcgr: GcgrCardSummary;
  contracting: ContractingCardSummary;
  "cost-model": CostModelCardSummary;
}

export type CardSummaryType = keyof CardSummaries;

const CARD_SUMMARY_KEYS: Record<CardSummaryType, DataCacheKey> = {
  estimate: "estimateCard",
  gcgr: "gcgrCard",
  contracting: "contractingCard",
  "cost-model": "costModelCard",
};

export const CARD_SUMMARY_CACHE_KEYS = Object.values(CARD_SUMMARY_KEYS);

const LEGACY_KEYS: Record<CardSummaryType, RetiredCacheKey[]> = {
  estimate: ["lastEstimate"],
  gcgr: [],
  contracting: ["lastBidGroups", "lastBidTabs"],
  "cost-model": [],
};

const saved: Partial<CardSummaries> = {};

const refreshStamp = (value: unknown): string | undefined => {
  const epochMs = excelDateToEpochMs(value, "local");
  return Number.isNaN(epochMs) ? undefined : new Date(epochMs).toISOString();
};

export const peekCardSummary = <T extends CardSummaryType>(type: T): CardSummaries[T] | undefined => saved[type];

const readLegacyCardSummary = async <T extends CardSummaryType>(type: T): Promise<CardSummaries[T] | undefined> => {
  if (type === "estimate") {
    const legacy = (await workbookCache.get("lastEstimate")) as EstimateData | null;
    const row = (await getEstimateReportData())[0];
    if (!legacy?.estimate?.id || legacy.estimate.id !== row?.estimate_id) return undefined;
    return estimateCardSummary(legacy.estimate, String(row.milestone ?? ""), row.last_updated) as CardSummaries[T];
  }
  if (type === "contracting") {
    const legacy = (await workbookCache.get("lastBidGroups")) as { projectId?: string; bidGroups?: BidGroup[] } | null;
    if (!legacy?.bidGroups?.length || legacy.projectId !== (await getLoadedProjectId())) return undefined;
    return contractingCardSummary(
      legacy.bidGroups,
      (await getContractingReportData())[0]?.last_updated
    ) as CardSummaries[T];
  }
  return undefined;
};

export const readCardSummary = async <T extends CardSummaryType>(type: T): Promise<CardSummaries[T] | undefined> => {
  if (saved[type]) return saved[type];
  const value =
    ((await workbookCache.get(CARD_SUMMARY_KEYS[type])) as CardSummaries[T] | null) ??
    (await readLegacyCardSummary(type));
  if (value) saved[type] = value;
  return value;
};

const saveCardSummary = async <T extends CardSummaryType>(type: T, summary: CardSummaries[T]): Promise<void> => {
  saved[type] = summary;
  await workbookCache.write({ set: { [CARD_SUMMARY_KEYS[type]]: summary }, delete: LEGACY_KEYS[type] });
};

export const deleteCardSummary = async (type: CardSummaryType): Promise<void> => {
  delete saved[type];
  await workbookCache.write({ delete: [CARD_SUMMARY_KEYS[type], ...LEGACY_KEYS[type]] });
};

export const forgetCardSummaries = (): void => {
  for (const type of Object.keys(saved) as CardSummaryType[]) delete saved[type];
};

const estimateCardSummary = (
  estimate: Estimate,
  milestoneName: string,
  lastRefreshedAt: unknown
): EstimateCardSummary => ({
  id: estimate.id,
  name: estimate.name,
  costOfTradesSubtotal: estimate.stats?.costOfTradesSubtotal ?? 0,
  grossArea: estimate.stats?.grossArea ?? 0,
  milestoneName,
  lastRefreshedAt: refreshStamp(lastRefreshedAt),
});

export const saveEstimateCardSummary = (
  estimate: Estimate,
  milestoneName: string,
  lastRefreshedAt: unknown
): Promise<void> => saveCardSummary("estimate", estimateCardSummary(estimate, milestoneName, lastRefreshedAt));

/**
 * The GC/GR split keys on the line item's `type`, matching the GCGR page's own totals. The GCGR
 * line-item report deliberately splits on `reporting_type` instead, which also routes items into
 * bid packages — so the two are expected to differ.
 */
export const saveGcgrCardSummary = (gcgr: GCGR, lastRefreshedAt: unknown): Promise<void> => {
  const payload = gcgr as GCGR & Record<string, any>;
  const lineItems: GCGRLineItem[] = payload.gcgr_detail_line_items ?? [];
  const staffDetails: StaffDetail[] = payload.gcgr_staff_details ?? [];
  const phases: Record<string, any>[] = payload.gcgr_phases ?? [];

  const sumByType = (type: string) =>
    lineItems.filter((item) => item.type === type).reduce((total, item) => total + (Number(item.total_cost) || 0), 0);

  const categoryByPhaseId = new Map(
    phases.map((phase) => [phase.id, String(phase.category ?? "").toLowerCase()] as const)
  );
  const sumStaffing = (category: string) =>
    staffDetails
      .filter((staff) => categoryByPhaseId.get(staff.phase) === category)
      .reduce((total, staff) => total + (Number(staff.grand_total) || 0), 0);

  return saveCardSummary("gcgr", {
    id: gcgr.id,
    name: gcgr.name,
    contractValue: gcgr.contract_value ?? 0,
    generalRequirements: sumByType("GR"),
    generalConditions: sumByType("GC"),
    preconStaffing: sumStaffing("precon"),
    constructionStaffing: sumStaffing("construction"),
    lastRefreshedAt: refreshStamp(lastRefreshedAt),
  });
};

const contractingCardSummary = (bidGroups: BidGroup[], lastRefreshedAt: unknown): ContractingCardSummary => ({
  projectedBuy: bidGroups.reduce((sum, bg) => sum + (bg.projected_buy ?? 0), 0),
  budget: bidGroups.reduce((sum, bg) => sum + (bg.budget ?? 0), 0),
  buyValue: bidGroups.reduce((sum, bg) => sum + (bg.buy_value ?? 0), 0),
  packageCount: bidGroups.length,
  awardedCount: bidGroups.filter((bg) => bg.award).length,
  lastRefreshedAt: refreshStamp(lastRefreshedAt),
});

export const saveContractingCardSummary = (bidGroups: BidGroup[], lastRefreshedAt: unknown): Promise<void> =>
  saveCardSummary("contracting", contractingCardSummary(bidGroups, lastRefreshedAt));

export const saveCostModelCardSummary = (
  costModel: CostModel,
  totals: CostModelTotalsLite,
  lastRefreshedAt: unknown
): Promise<void> =>
  saveCardSummary("cost-model", {
    id: costModel.id,
    label: costModelLabel(costModel),
    grandTotal: totals.grandTotal,
    grossArea: totals.grossArea,
    costPerSqft: totals.costPerSqft,
    lastRefreshedAt: refreshStamp(lastRefreshedAt),
  });
